import {
  FormFields,
  GetFormValueFunction,
  SetFormValueFunction,
} from "features/shared/forms/hooks/useForm";
import { produce } from "immer";
import _ from "lodash";
import { ContractingDTO } from "features/contracting/dtos/ContractingDTO";
import { ContractingFormType } from "features/contracting/enums/contractingFormType";
import { formatDate } from "features/shared/helpers/Formatters";
import { TFunction } from "i18next";
import CustomerTypeStep from "features/contracting/models/steps/customerType/CustomerTypeStep";
import { Commodity } from "features/shared/enums/commodity";
import { CustomerType } from "features/customer/enums/customerType";
import ContractingStepConfiguration from "features/contracting/models/ContractingStepConfiguration";
import ContractingStepData from "features/contracting/models/ContractingStepData";
import ContractingStepRoute from "features/contracting/models/ContractingStepRoute";
import ContractingSteps from "features/contracting/models/ContractingSteps";

export const assignDefaultValue = (
  formValues: FormFields,
  inputName: string,
  inputValue: any
): FormFields => {
  return produce(formValues, (valuesDraft) => {
    const value = _.get(formValues, inputName);
    if (_.isNil(value)) {
      _.set(valuesDraft, inputName, inputValue);
    }
  });
};

export const getTypedStepValues = <TValues extends object>(
  contracting: ContractingDTO,
  type: ContractingFormType
): TValues => {
  const stepValues = contracting.steps[type].values;
  return stepValues as TValues;
};

export const getFormattedDate = (value: string) => {
  const date = formatDate(value);
  return date ?? "-";
};

export const getBoolText = (value: boolean, tBool: TFunction) => {
  const boolStr = _.capitalize(String(value));
  return tBool(boolStr);
};

export const getContractingAddressName = (
  contracting: ContractingDTO,
  tAddress: TFunction,
  lowerCase?: boolean,
  prefix?: string
) => {
  const customerType = getContractingCustomerType(contracting);

  const part1 = prefix ? `${prefix} ` : "";
  const part2 = lowerCase
    ? tAddress(customerType).toLocaleLowerCase()
    : tAddress(customerType);
  return `${part1}${part2}`;
};

export const getContractingCustomerType = (
  contracting: ContractingDTO
): CustomerType => {
  const typeStep = getTypedStepValues<CustomerTypeStep>(
    contracting,
    ContractingFormType.CustomerType
  );
  return typeStep.customerType ?? "Undefined";
};

export const getContractingCommodity = (
  contracting: ContractingDTO
): Commodity => {
  const typeStep = getTypedStepValues<CustomerTypeStep>(
    contracting,
    ContractingFormType.CustomerType
  );
  return typeStep.commodity ?? "Undefined";
};

export const assignDefaultFormValue = (
  inputName: string,
  inputValue: any,
  getFormValue: GetFormValueFunction,
  setFormValue: SetFormValueFunction
) => {
  const value = getFormValue(inputName);
  if (!value) {
    setFormValue(inputName, inputValue);
  }
};

export const createDefaultContracting = (
  stepsConfig: ContractingStepConfiguration[],
  actualStepRoute: ContractingStepRoute,
  initValues?: ContractingSteps
): ContractingDTO => {
  let stepsData: Record<string, ContractingStepData> = {};
  let route = stepsConfig[0].route; // first step is default route

  for (let i = 0; i < stepsConfig.length; i++) {
    const step = stepsConfig[i];

    let stepData: ContractingStepData = {
      touched: false,
      valid: false,
      disabled: false,
      values: {},
    };
    // setting default step data
    if (initValues) {
      let defaultStep = initValues[step.component];
      if (defaultStep) {
        stepData = defaultStep;
      }
    }
    // setting default route
    if (step.component === actualStepRoute.step) {
      route = step.route;
    }

    stepsData[step.component] = stepData;
  }

  const progress: ContractingDTO = {
    steps: stepsData,
    actualStepRoute: `${actualStepRoute.routePrefix}/${route}`,
  };

  return progress;
};

export const hasContractingIdInQueryParams = (queryParams: URLSearchParams): boolean => {
  const contratingId = queryParams.get("contractingId");
  if (contratingId != null) {
    return true;
  }

  return false;
};